// Bookmarklet runtime: takes a JSON profile blob and tries to autofill common
// Workday / Greenhouse / Lever / Ashby inputs by matching input id, name,
// label, placeholder, or aria-label against a list of known synonyms per field.
//
// This file is read as a STRING and wrapped into a `javascript:` URL — keep it
// dependency-free, side-effect-only, and small.

export const BOOKMARKLET_RUNTIME = `(function(){
  try {
    var data = window.__JOBYODA_PROFILE__;
    if (!data) { alert("JobYoda: no profile data found in bookmarklet."); return; }
    var MAP = {
      firstName:    ["first_name","firstname","first-name","given-name","fname","legalNameFirstName"],
      lastName:     ["last_name","lastname","last-name","family-name","lname","legalNameLastName"],
      fullName:     ["full_name","fullname","name","candidate_name","preferredName"],
      email:        ["email","email_address","e-mail","candidate_email"],
      phone:        ["phone","phone_number","mobile","tel","telephone","contactPhone"],
      pronouns:     ["pronouns","preferred_pronouns","gender_pronouns"],
      location:     ["location","city","current_location","candidate_location","address"],
      headline:     ["headline","current_title","currentJobTitle","job_title","title"],
      summary:      ["summary","about","bio","cover_letter","why_us","why_interested"],
      linkedin:     ["linkedin","linkedin_url","linkedinprofile","urls.linkedin"],
      github:       ["github","github_url","website","websiteurl","urls.github"],
      portfolio:    ["portfolio","portfolio_url","website_url","urls.portfolio","personal_website"],
      currentCompany:["current_company","mostRecentCompany","employer","company"],
      currentTitle: ["current_title","mostRecentJobTitle","most_recent_title"],
      skills:       ["skills","key_skills","tags","technicalSkills"],
      // Work authorization — text fields, dropdowns, and yes/no questions
      workAuthorization: ["work_authorization","authorized_to_work","work_auth","legally_authorized","right_to_work","eligible_to_work"],
      sponsorship:  ["sponsorship","require_sponsorship","need_sponsorship","visa_sponsorship","requires_sponsorship"],
      // Voluntary self-identification (EEO)
      gender:       ["gender","gender_identity","sex"],
      race:         ["race","ethnicity","race_ethnicity","ethnic_background"],
      hispanicLatino:["hispanic","latino","hispanic_or_latino","hispanic_latino"],
      veteran:      ["veteran","veteran_status","military_status"],
      disability:   ["disability","disability_status"],
      sexualOrientation: ["sexual_orientation","orientation"]
    };
    function norm(s){ return (s||"").toString().toLowerCase().replace(/[^a-z0-9]/g,""); }
    function labelFor(el){
      var lbl = "";
      if (el.id) {
        var l = document.querySelector('label[for="'+CSS.escape(el.id)+'"]');
        if (l) lbl = l.innerText || l.textContent || "";
      }
      var p = el.closest("label");
      if (!lbl && p) lbl = p.innerText || p.textContent || "";
      return lbl;
    }
    function setVal(el, v){
      var tag = el.tagName;
      var proto;
      if (tag === "TEXTAREA") proto = window.HTMLTextAreaElement.prototype;
      else if (tag === "SELECT") proto = window.HTMLSelectElement.prototype;
      else proto = window.HTMLInputElement.prototype;
      var setter = Object.getOwnPropertyDescriptor(proto, "value");
      if (setter && setter.set) setter.set.call(el, v); else el.value = v;
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.dispatchEvent(new Event("change", { bubbles: true }));
      el.dispatchEvent(new Event("blur", { bubbles: true }));
    }
    function matches(el, syns){
      var hay = [el.id, el.name, el.getAttribute("placeholder"), el.getAttribute("aria-label"),
                 el.getAttribute("data-automation-id"), labelFor(el)]
                 .map(norm).join("|");
      for (var i=0;i<syns.length;i++){ if (hay.indexOf(norm(syns[i])) !== -1) return true; }
      return false;
    }
    var inputs = Array.prototype.slice.call(document.querySelectorAll("input,textarea,select"));
    var filled = 0; var skipped = 0;
    inputs.forEach(function(el){
      if (el.type === "hidden" || el.type === "submit" || el.type === "button" ||
          el.type === "checkbox" || el.type === "radio" || el.type === "file") { return; }
      if (el.disabled || el.readOnly) { return; }
      if (el.value && el.value.trim().length > 0) { skipped++; return; }
      for (var key in MAP) {
        if (!Object.prototype.hasOwnProperty.call(MAP, key)) continue;
        var v = data[key];
        if (!v) continue;
        if (matches(el, MAP[key])) {
          try { setVal(el, v); filled++; } catch(e) {}
          break;
        }
      }
    });
    var msg = "JobYoda autofill: " + filled + " field" + (filled===1?"":"s") +
              " filled, " + skipped + " left untouched (already had a value).";
    var bar = document.createElement("div");
    bar.textContent = msg;
    bar.style.cssText = "position:fixed;z-index:2147483647;bottom:16px;left:50%;transform:translateX(-50%);"+
      "background:#0f172a;color:#fff;padding:10px 14px;border-radius:8px;font:13px system-ui,sans-serif;"+
      "box-shadow:0 8px 24px rgba(0,0,0,.25);";
    document.body.appendChild(bar);
    setTimeout(function(){ bar.remove(); }, 4000);
  } catch (e) {
    alert("JobYoda autofill failed: " + (e && e.message ? e.message : e));
  }
})();`;

export type BookmarkletProfile = {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  pronouns?: string;
  location?: string;
  headline?: string;
  summary?: string;
  linkedin?: string;
  github?: string;
  portfolio?: string;
  currentCompany?: string;
  currentTitle?: string;
  skills?: string;
  workAuthorization?: string;
  sponsorship?: string;
  gender?: string;
  race?: string;
  hispanicLatino?: string;
  veteran?: string;
  disability?: string;
  sexualOrientation?: string;
};

/**
 * Build the final `javascript:` URL by inlining the profile JSON ahead of the runtime.
 * The result is what the user drags onto their bookmarks bar.
 */
export function buildBookmarkletHref(profile: BookmarkletProfile): string {
  // Strip undefined / empty
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(profile)) {
    if (typeof v === "string" && v.trim()) clean[k] = v.trim();
  }
  const json = JSON.stringify(clean);
  // Wrap so __JOBYODA_PROFILE__ is set BEFORE runtime executes.
  const src = `window.__JOBYODA_PROFILE__=${json};${BOOKMARKLET_RUNTIME}`;
  // Single-line, percent-encoded for the href. Browsers strip newlines from
  // javascript: URLs but encoding keeps quotes/spaces safe.
  return `javascript:${encodeURIComponent(src)}`;
}
